import { type ITravelerInfo } from '@pcln/horizon'
import { useCallback, useId, useState, type FormEvent } from 'react'

import type { LOCATION_SEARCH_TYPE } from '@/types'

import { hasAiSearchMinLength } from './aiSearchConstants'
import { useAiSearchSuggestions, type SuggestionChip } from './useAiSearchSuggestions'

const DEFAULT_TRAVELERS: ITravelerInfo = {
  adults: 2,
  children: 0,
  childrenAges: {},
  rooms: 1,
  lapInfants: 0,
}

export type AiSearchSubmitParams = {
  query: string
  startDate: string
  endDate: string
  travelers: ITravelerInfo
  /**
   * The suggestion already resolved for `query` when it exactly matches a fetched chip.
   * Submitting should reuse this instead of re-resolving `query` from scratch — a fresh
   * AI search on the chip's generated label text can rank a different top match (e.g. a
   * different city) than the item that produced that label.
   */
  resolvedLocation?: LOCATION_SEARCH_TYPE | null
}

type UseAiSearchFormParams = {
  initialStartDate?: string
  initialEndDate?: string
  initialTravelers?: ITravelerInfo
  onSubmitQuery: (params: AiSearchSubmitParams) => Promise<void> | void
}

export function useAiSearchForm({
  initialStartDate = '',
  initialEndDate = '',
  initialTravelers = DEFAULT_TRAVELERS,
  onSubmitQuery,
}: UseAiSearchFormParams) {
  const [inputValue, setInputValue] = useState('')
  const [startDate, setStartDate] = useState(initialStartDate)
  const [endDate, setEndDate] = useState(initialEndDate)
  const [travelers, setTravelers] = useState<ITravelerInfo>(initialTravelers)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedChipKey, setSelectedChipKey] = useState<string | null>(null)
  const [hasSubmitError, setHasSubmitError] = useState(false)

  const inputId = useId()
  const helperTextId = useId()
  const submitErrorId = useId()
  const recommendationsLabelId = useId()
  const recommendationsStatusId = useId()
  const emptyRecommendationsId = useId()
  const errorRecommendationsId = useId()

  const {
    fetchQuery,
    suggestionChips,
    hasTypedQuery,
    status,
    isSubmitDisabledBySuggestions,
    onTypedQueryChange,
    cancelPendingFetch,
  } = useAiSearchSuggestions()

  const hasQuery = hasAiSearchMinLength(inputValue)

  const inputDescribedBy = [
    helperTextId,
    hasSubmitError ? submitErrorId : null,
    status === 'empty' ? emptyRecommendationsId : null,
    status === 'error' ? errorRecommendationsId : null,
    recommendationsStatusId,
  ]
    .filter(Boolean)
    .join(' ')

  const handleSubmit = useCallback(
    async (query: string) => {
      const trimmedQuery = query.trim()
      if (!hasAiSearchMinLength(trimmedQuery) || isSubmitting) return
      if (hasAiSearchMinLength(fetchQuery) && suggestionChips.length === 0) return

      // Reuse the suggestion already fetched for this exact chip/query instead of letting
      // the submit handler re-resolve it — re-querying the chip's generated label text can
      // rank a different top match than the item that produced it.
      const matchedChip = suggestionChips.find((chip) => chip.label === trimmedQuery)

      setHasSubmitError(false)
      setIsSubmitting(true)
      try {
        await onSubmitQuery({
          query: trimmedQuery,
          startDate,
          endDate,
          travelers,
          resolvedLocation: matchedChip?.suggestion ?? null,
        })
      } catch {
        setHasSubmitError(true)
      } finally {
        setIsSubmitting(false)
      }
    },
    [endDate, fetchQuery, isSubmitting, onSubmitQuery, startDate, suggestionChips, travelers],
  )

  const handleInputChange = useCallback(
    (value: string) => {
      setHasSubmitError(false)
      setInputValue(value)
      setSelectedChipKey(null)
      onTypedQueryChange(value)
    },
    [onTypedQueryChange],
  )

  const handleDatesChange = useCallback((nextStartDate: string, nextEndDate: string) => {
    setStartDate(nextStartDate)
    setEndDate(nextEndDate)
  }, [])

  const handleChipSelect = useCallback(
    (chip: SuggestionChip) => {
      setHasSubmitError(false)
      cancelPendingFetch()
      setInputValue(chip.label)
      setSelectedChipKey(chip.key)
      document.getElementById(inputId)?.focus()
    },
    [cancelPendingFetch, inputId],
  )

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      void handleSubmit(inputValue)
    },
    [handleSubmit, inputValue],
  )

  const handleInputSubmit = useCallback(
    (query: string) => {
      void handleSubmit(query)
    },
    [handleSubmit],
  )

  return {
    inputValue,
    startDate,
    endDate,
    travelers,
    isSubmitting,
    selectedChipKey,
    hasSubmitError,
    hasQuery,
    inputId,
    helperTextId,
    submitErrorId,
    recommendationsLabelId,
    recommendationsStatusId,
    emptyRecommendationsId,
    errorRecommendationsId,
    inputDescribedBy,
    fetchQuery,
    suggestionChips,
    hasTypedQuery,
    status,
    isSubmitDisabledBySuggestions,
    setTravelers,
    handleFormSubmit,
    handleInputSubmit,
    handleInputChange,
    handleDatesChange,
    handleChipSelect,
  }
}
